import { DeleteIcon } from '@chakra-ui/icons';
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Badge,
    Box,
    Button,
    Flex,
    IconButton,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from '@tanstack/react-table';
import PageCard from 'components/page-card';
import SortIndicator from 'components/table/SortIndicator';
import { useMemo, useRef, useState } from 'react';
import { MdPersonAdd } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../components/Loader';
import ErrorState from '../../../components/states/ErrorState';
import { translate } from '../../../i18n';
import { tableHeaderTextProps, tableProps, tableThProps, tableThFlexProps, tableRowProps, tableCellProps } from '../../../theme/constants';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useDeleteCoupon } from '../hooks/useDeleteCoupon';
import { CouponDto, useListCoupons } from '../hooks/useListCoupons';
import AssignCouponModal from './AssignCouponModal';
import CreateCouponModal from './CreateCouponModal';

const columnHelper = createColumnHelper<CouponDto>();

const PAYABLE_TYPE_LABELS: Record<string, string> = {
    COURT_BOOKING: translate('coupon.applicable_court_booking'),
    GAME_JOINING: translate('coupon.applicable_game_joining'),
    EVENT_JOINING: translate('coupon.applicable_event_joining'),
    COACHING_SESSION: translate('coupon.applicable_coaching_session'),
};

export default function CouponList() {
    const { selectedOrganization } = useSelectedOrganization();
    const { data, isInitialLoading, isError, refetch } = useListCoupons(selectedOrganization?.id || '');
    // `enabled: !!organizationId`, so the raw `isLoading` this used to return a
    // <Loader/> on is `true` forever whenever no organization is selected — a
    // state run 100 measured as reachable for a whole session.
    const readState = classifyRead(isError, isInitialLoading, data);
    const [sorting, setSorting] = useState<SortingState>([]);
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const toast = useToast();
    const navigate = useNavigate();

    // Create modal
    const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();

    // Assign modal
    const { isOpen: isAssignOpen, onOpen: onAssignOpen, onClose: onAssignClose } = useDisclosure();
    const [assigningCoupon, setAssigningCoupon] = useState<CouponDto | null>(null);

    // Delete modal
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const [deletingCoupon, setDeletingCoupon] = useState<CouponDto | null>(null);
    const cancelButtonRef = useRef<HTMLButtonElement>(null);
    const { mutateAsync: deleteCoupon, isLoading: isDeleting } = useDeleteCoupon();

    const handleAssign = (coupon: CouponDto) => {
        setAssigningCoupon(coupon);
        onAssignOpen();
    };

    const handleDelete = (coupon: CouponDto) => {
        setDeletingCoupon(coupon);
        onDeleteOpen();
    };

    const handleConfirmDelete = async () => {
        if (!deletingCoupon) return;
        try {
            await deleteCoupon(deletingCoupon.id);
            toast({
                title: translate('coupon.deleted_successfully'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            onDeleteClose();
            setDeletingCoupon(null);
        } catch (error: any) {
            toast({
                title: translate('coupon.delete_error'),
                description: error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const columns = useMemo(() => [
        columnHelper.accessor('name', {
            id: 'name',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('coupon.name')}
                </Text>
            ),
            cell: (info) => (
                <Text color={textColor} fontSize='sm' fontWeight='700'>
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor('discountType', {
            id: 'discount',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('coupon.discount')}
                </Text>
            ),
            cell: (info) => {
                const row = info.row.original;
                const label = row.discountType === 'PERCENTAGE'
                    ? `${row.discountValue}%`
                    : `${row.discountValue} ${(row.currency || '').toUpperCase()}`;
                return (
                    <Badge colorScheme={row.discountType === 'PERCENTAGE' ? 'purple' : 'brand'}>
                        {label}
                    </Badge>
                );
            },
        }),
        columnHelper.accessor('applicableTo', {
            id: 'applicableTo',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('coupon.applicable_to')}
                </Text>
            ),
            cell: (info) => (
                <Flex gap={1} wrap="wrap">
                    {info.getValue().map((type: string) => (
                        <Badge key={type} size="sm" colorScheme="gray" fontSize="xs">
                            {PAYABLE_TYPE_LABELS[type] || type}
                        </Badge>
                    ))}
                </Flex>
            ),
        }),
        columnHelper.accessor('expiresAt', {
            id: 'expiresAt',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('coupon.expires_at')}
                </Text>
            ),
            cell: (info) => {
                const date = new Date(info.getValue());
                const isExpired = date < new Date();
                return (
                    <Text color={isExpired ? 'red.500' : textColor} fontSize='sm' fontWeight={isExpired ? '700' : '400'}>
                        {date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        {isExpired && ` (${translate('coupon.expired')})`}
                    </Text>
                );
            },
        }),
        columnHelper.accessor('maxUsagePerUser', {
            id: 'maxUsage',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('coupon.max_uses')}
                </Text>
            ),
            cell: (info) => (
                <Text color={textColor} fontSize='sm'>
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.display({
            id: 'used_attributed',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('coupon.used_attributed')}
                </Text>
            ),
            cell: (info) => {
                const used = info.row.original.totalUsageCount ?? 0;
                const attributed = info.row.original.assignedCount ?? 0;
                return (
                    <Text color={textColor} fontSize='sm'>
                        {used} / {attributed}
                    </Text>
                );
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: () => (
                <Text {...tableHeaderTextProps}>
                    {translate('coupon.actions')}
                </Text>
            ),
            cell: (info) => (
                <Flex align='center' gap={2}>
                    <IconButton
                        aria-label={translate('coupon.assign_coupon')}
                        icon={<MdPersonAdd />}
                        size="sm"
                        variant="ghost"
                        colorScheme="brand"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleAssign(info.row.original);
                        }}
                    />
                    <IconButton
                        aria-label={translate('coupon.delete_coupon')}
                        icon={<DeleteIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(info.row.original);
                        }}
                    />
                </Flex>
            ),
        }),
    ], [textColor]);

    const table = useReactTable({
        data: data || [],
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (readState === 'never-issued') {
        return (
            <PageCard title={translate('coupon.coupons')}>
                <ScopePrompt gate='organization' />
            </PageCard>
        );
    }

    if (readState === 'loading') {
        return <Loader isLoading />;
    }

    // A refused read used to fall through to `(!data || data.length === 0)` and
    // render "No coupons yet. Create one to get started." — an invitation to
    // create a duplicate of a coupon that already exists. The create button is
    // deliberately NOT offered here for the same reason: we do not know what is
    // there. See `coupon-read-error-gate.test.ts`.
    // `unreadable`, not the raw `isError`: this arm is an EARLY RETURN over the
    // whole card, so a BACKGROUND refetch that fails while coupons are on screen
    // replaced them with an error block — the R96-3 blanking in its purest form.
    // A refused read with nothing to show still lands here.
    if (readState === 'unreadable') {
        return (
            <PageCard title={translate('coupon.coupons')}>
                <ErrorState
                    message={translate('common.unexpected_error')}
                    onRetry={() => { void refetch(); }}
                />
            </PageCard>
        );
    }

    return (
        <PageCard
            title={translate('coupon.coupons')}
            rightHeader={
                <Button colorScheme="brand" onClick={onCreateOpen} size="sm">
                    {translate('coupon.create_coupon')}
                </Button>
            }
        >
            <Box>
                <Table {...tableProps}>
                    <Thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <Tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <Th
                                        {...tableThProps}
                                        key={header.id}
                                        borderColor={borderColor}
                                        cursor='pointer'
                                        onClick={header.column.getToggleSortingHandler()}
                                    >
                                        <Flex
                                            {...tableThFlexProps}>
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            <SortIndicator direction={header.column.getIsSorted()} />
                                        </Flex>
                                    </Th>
                                ))}
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                        {table.getRowModel().rows.map(row => (
                            <Tr
                                key={row.id}
                                {...tableRowProps}
                                onClick={() => navigate(`/admin/coupons/${row.original.id}`)}
                            >
                                {row.getVisibleCells().map(cell => (
                                    <Td
                                        key={cell.id}
                                        {...tableCellProps}
                                        borderColor={borderColor}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </Td>
                                ))}
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
                {(!data || data.length === 0) && (
                    <Flex justify="center" py={8}>
                        <Text color="gray.400">{translate('coupon.no_coupons')}</Text>
                    </Flex>
                )}
            </Box>

            <CreateCouponModal isOpen={isCreateOpen} onClose={onCreateClose} />

            {assigningCoupon && (
                <AssignCouponModal
                    couponId={assigningCoupon.id}
                    couponName={assigningCoupon.name}
                    isOpen={isAssignOpen}
                    onClose={() => {
                        onAssignClose();
                        setAssigningCoupon(null);
                    }}
                />
            )}

            <AlertDialog
                isOpen={isDeleteOpen}
                leastDestructiveRef={cancelButtonRef}
                onClose={onDeleteClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                            {translate('coupon.delete_coupon')}
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            {translate('coupon.delete_confirm').replace('{name}', deletingCoupon?.name || '')}
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelButtonRef} onClick={onDeleteClose}>
                                {translate('common.cancel')}
                            </Button>
                            <Button colorScheme='red' onClick={handleConfirmDelete} ml={3} isLoading={isDeleting}>
                                {translate('common.delete')}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </PageCard>
    );
}
